import { Navigate, Route, Routes } from 'react-router';
import { SocialStudioPage } from '../features/socialStudio/SocialStudioPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { AuthProvider } from '../features/auth/AuthProvider';
import { AuthCallbackPage, SignInPage } from '../features/auth/AuthPages';
import {
  PublicOnlyRoute,
  RequireSuperAdmin,
  RequireSupplier,
  RequireTap,
  RequireTapAdmin,
} from '../features/routing/boundaries';
import { AppErrorBoundary } from '../features/routing/ErrorBoundary';
import { SupplierLayout, TapLayout } from '../layouts/Shells';
import { InboxPage } from '../features/notifications/InboxPage';
import { FilesPage } from '../features/files/FilesPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { NotFoundPage } from '../pages/Placeholders';
import { LeavePage } from '../features/leave/LeavePage';
import { SupplierExchangePage } from '../features/supplierExchange/SupplierExchangePage';
import { HomeSection } from '../features/home/HomeSection';
import { ReviewPortalPage } from '../features/reviewPortal/ReviewPortalPage';
import { ScoreEntryPage } from '../features/marketingScoreboard/ScoreEntryPage';
import { BriefPortalPage } from '../features/briefPortal/BriefPortalPage';
import { PagePulsePage } from '../features/pagePulse/PagePulsePage';
import { MarketingScoreboardPage } from '../features/marketingScoreboard/MarketingScoreboardPage';
import { PagePulsePortalPage } from '../features/pagePulse/PagePulsePortalPage';
import { LinkedInFunnelPage } from '../features/huddlePortal/LinkedInFunnelPage';
import { HuddlePage } from '../features/huddlePortal/HuddlePage';
import { ImageLightbox } from '../components/ImageLightbox';
import { TgPage } from '../features/tgTeam/TgPage';
import { NotesPage } from '../features/notes/NotesPage';
import { PersonalTasksPage } from '../features/personal/PersonalTasksPage';
import { MyDayPage } from '../features/myDay/MyDayPage';
import { ProjectPage } from '../features/projects/ProjectPage';
import { ProjectsPage } from '../features/projects/ProjectsPage';
import { ContentStudioPage } from '../features/contentStudio/ContentStudioPage';
import { TrackerPage } from '../features/tracker/TrackerPage';
import { CgOrderPage } from '../features/cgOrders/CgOrderPage';
import { CgOrdersPage } from '../features/cgOrders/CgOrdersPage';
import { CeoBriefPage } from '../features/ceoBrief/CeoBriefPage';
import { DealsPage } from '../features/deals/DealsPage';
import { SalesTasksPage } from '../features/salesTasks/SalesTasksPage';
import { AdminOrganizationsPage } from '../features/supplierOrganizations/AdminOrganizationsPage';
import { SupplierPortalPage } from '../features/supplierOrganizations/SupplierPortalPage';
import { AdminGuidePage } from '../features/guide/AdminGuidePage';
import { SupplierGuidePage } from '../features/guide/SupplierGuidePage';
import { MessagesPage } from '../features/messages/MessagesPage';
import { HuddleLayout } from '../features/huddles/HuddleProvider';
import type { SupabaseClient } from '../lib/supabase';

const HiringPage = lazy(() =>
  import('../features/hiring/HiringPage').then((module) => ({ default: module.HiringPage })),
);

// Tide is Kyu's own app. Its code and styles load only when /tide or /tide-demo opens (note N5).
const TideRoute = lazy(() =>
  import('../features/tide/TideRoute').then((module) => ({ default: module.TideRoute })),
);
const TideDemoRoute = lazy(() =>
  import('../features/tide/TideDemoRoute').then((module) => ({
    default: module.TideDemoRoute,
  })),
);

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<SignInPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Route>

      {/* The public routes. Two of them now, both outside every boundary and
          every layout for the same reason: the person opening one has no
          account and must never see the inside of TAP's tooling. Each carries
          its own auth as a token in the URL, checked server-side.

          /review is the client sign-off portal, against review_token_order.

          /brief is the CEO Brief, against use_brief_token. Alex met a Google
          consent screen on the way in and closed it, twice; a brief nobody
          reads is a brief that was not written. Read-only, and the server has
          no write path behind that token at all. /pulse is the Page Pulse read link. */}
      <Route path="/review/:token" element={<ReviewPortalPage />} />
      <Route path="/scoreboard-entry/:token" element={<ScoreEntryPage />} />
      <Route path="/brief/:token" element={<BriefPortalPage />} />
      <Route path="/pulse/:token" element={<PagePulsePortalPage />} />
      <Route
        path="/huddle/:token"
        element={
          <>
            <HuddlePage />
            {/* Console images open here, on the same page, never in a new tab. */}
            <ImageLightbox />
          </>
        }
      />
      <Route path="/huddle/:token/linkedin" element={<LinkedInFunnelPage />} />
      {/* /tg is Kyu's Telegram Mini App. No Hub login and no layout: it proves
          itself with Telegram's signed initData and calls only tg-team. */}
      <Route path="/tg" element={<TgPage />} />

      <Route element={<HuddleLayout />}>
        <Route element={<RequireTap />}>
          {/* Tide: Kyu's installed app. /tide is Kyu only, live data. /tide-demo
            is fictional fixtures, still behind the TAP sign-in. */}
          <Route
            path="/tide/*"
            element={
              <Suspense fallback={<p>Loading Tide...</p>}>
                <TideRoute />
              </Suspense>
            }
          />
          <Route
            path="/tide-demo/*"
            element={
              <Suspense fallback={<p>Loading Tide...</p>}>
                <TideDemoRoute />
              </Suspense>
            }
          />
          <Route element={<TapLayout />}>
            {/* Home is a section with three sub-tabs, not a page. Both patterns
              point at the same component; it reads the tab off the path, so
              an unknown sub-path falls back to the dashboard rather than
              rendering an empty shell. */}
            <Route path="/home" element={<HomeSection />} />
            <Route path="/home/:tab" element={<HomeSection />} />
            {/* The two addresses that existed before the restructure. / is the
              app root and half the team's browser opens there; /my-tasks was a
              real page for months and is in bookmarks, in Slack, and in a link
              inside Home itself. */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/my-tasks" element={<Navigate to="/home/tasks" replace />} />
            <Route path="/notes" element={<NotesPage />} />
            {/* My Notes was a Home sub-tab for a day before getting its own
              sidebar item. */}
            <Route path="/home/notes" element={<Navigate to="/notes" replace />} />
            <Route path="/my-day" element={<MyDayPage />} />
            {/* Personal Tasks is the same rows without the day's shape. It left
              the sidebar when My Day arrived (drop 106) and still answers
              here, unlinked, until Kyu says it goes. */}
            <Route path="/personal" element={<PersonalTasksPage />} />
            {/* The sidebar shipped pointing here for one drop, so anybody who
              bookmarked it or was mid-session keeps working instead of hitting
              a 404 they cannot explain. */}
            <Route path="/personal-tasks" element={<Navigate to="/personal" replace />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<ProjectPage />} />
            <Route path="/studio" element={<ContentStudioPage />} />
            <Route path="/social-studio" element={<SocialStudioPage />} />
            <Route path="/tracker" element={<TrackerPage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/sales-tasks" element={<SalesTasksPage />} />
            <Route
              path="/hiring"
              element={
                <Suspense fallback={<p>Loading hiring...</p>}>
                  <HiringPage />
                </Suspense>
              }
            />
            <Route path="/ceo-brief" element={<CeoBriefPage />} />
            <Route path="/ceo-brief/:briefDate" element={<CeoBriefPage />} />
            <Route path="/page-pulse" element={<PagePulsePage />} />
            <Route path="/marketing-scoreboard" element={<MarketingScoreboardPage />} />
            <Route path="/cg-orders" element={<CgOrdersPage />} />
            {/* One URL per job. The drawer stays for quick edits and links here
              for the whole case file — see CgOrderPage. */}
            <Route path="/cg-orders/:orderId" element={<CgOrderPage />} />
            <Route path="/leaves" element={<LeavePage />} />
            <Route path="/supplier-reviews" element={<SupplierExchangePage />} />
            <Route path="/files" element={<FilesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>

        <Route element={<RequireTapAdmin />}>
          <Route element={<TapLayout />}>
            <Route path="/admin" element={<AdminOrganizationsPage />} />
          </Route>
        </Route>

        {/* The master manual is a sibling of /admin rather than a child, because
          tap_admin and super_admin are different things: every super admin is
          let through is_tap_admin() by the master key, but a tap_admin is NOT
          a super admin and must not read this. Nesting it under
          RequireTapAdmin would have admitted the whole admin group. */}
        <Route element={<RequireSuperAdmin />}>
          <Route element={<TapLayout />}>
            <Route path="/admin/guide" element={<AdminGuidePage />} />
          </Route>
        </Route>
      </Route>
      <Route element={<RequireSupplier />}>
        <Route element={<SupplierLayout />}>
          <Route path="/supplier" element={<SupplierPortalPage />} />
          <Route path="/supplier/guide" element={<SupplierGuidePage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function App({ client }: { client: SupabaseClient }) {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider client={client}>
          <AppRoutes />
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
